
import React, { useState } from 'react';

interface Recipe {
  id: string;
  name: string;
  category: string;
  ingredients: string[];
  instructions: string[];
  prepTime: number;
  cookTime: number;
  servings: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  image: string;
  tips?: string;
}

export default function RecipeBook() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);

  const [recipes, setRecipes] = useState<Recipe[]>([
    {
      id: 'tonkotsu-ramen',
      name: 'Tonkotsu Ramen Deluxe',
      category: 'Ramen',
      ingredients: [
        '500g tulang babi (untuk kuah)',
        '200g chashu pork',
        '2 butir telur ajitsuke',
        '100g bambu menma',
        '50g daun bawang',
        '2 lembar nori',
        '200g mie ramen',
        '2 sdm miso putih',
        '1 sdm sake',
        'Bawang putih cincang'
      ],
      instructions: [
        'Rebus tulang babi selama 12 jam untuk kuah tonkotsu yang kental',
        'Masak chashu dengan soy sauce dan mirin selama 2 jam',
        'Buat telur ajitsuke dengan marinasi 6 jam',
        'Rebus mie ramen hingga al dente (2 menit)',
        'Panaskan kuah tonkotsu, tambahkan miso',
        'Susun mie di mangkuk, tuang kuah panas',
        'Tambahkan chashu, telur, menma, dan nori',
        'Taburi daun bawang dan sajikan segera'
      ],
      prepTime: 30,
      cookTime: 120,
      servings: 2,
      difficulty: 'Hard',
      image: 'https://public.readdy.ai/ai/img_res/c77f0185188553ec4b99a3a61e928ea6.jpg',
      tips: 'Kuah tonkotsu yang sempurna membutuhkan waktu rebus minimal 12 jam. Jaga api kecil agar tidak keruh.'
    },
    {
      id: 'sushi-omakase',
      name: 'Omakase Sushi Set Premium',
      category: 'Sushi',
      ingredients: [
        '300g nasi sushi (shari)',
        '2 sdm rice vinegar',
        '1 sdm gula',
        '1 tsp garam',
        '100g salmon sashimi grade',
        '100g tuna sashimi grade',
        '50g yellowtail',
        '30g uni (sea urchin)',
        '6 ekor udang',
        'Wasabi segar',
        'Jahe acar (gari)'
      ],
      instructions: [
        'Masak nasi dengan perbandingan air 1:1.2',
        'Buat sushi-zu dengan campuran vinegar, gula, garam',
        'Aduk nasi panas dengan sushi-zu, kipas hingga suhu ruang',
        'Potong ikan dengan teknik yanagiba, tebal 5mm',
        'Bentuk shari dengan tangan basah, 20g per piece',
        'Letakkan ikan di atas shari, tekan lembut',
        'Buat udang ebi dengan teknik khusus',
        'Susun sushi di wooden board dengan garnish'
      ],
      prepTime: 45,
      cookTime: 30,
      servings: 2,
      difficulty: 'Hard',
      image: 'https://readdy.ai/api/search-image?query=Professional%20sushi%20chef%20preparing%20omakase%20set%2C%20fresh%20sashimi%20grade%20fish%2C%20perfectly%20seasoned%20sushi%20rice%2C%20nigiri%20assembly%2C%20traditional%20wooden%20cutting%20board%2C%20sushi%20knife%2C%20restaurant%20kitchen%2C%20Japanese%20culinary%20artistry&width=400&height=300&seq=recipe-sushi-omakase&orientation=landscape',
      tips: 'Suhu nasi sushi harus sama dengan suhu tubuh. Gunakan tangan basah saat membentuk shari.'
    },
    {
      id: 'tempura-premium',
      name: 'Tempura Premium Set',
      category: 'Tempura',
      ingredients: [
        '8 ekor udang besar',
        '100g sayuran (terong, paprika, buncis)',
        '150g tepung tempura',
        '200ml air es',
        '1 kuning telur',
        'Minyak untuk deep fry',
        '100ml tentsuyu sauce',
        '2 sdm mirin',
        '2 sdm soy sauce',
        '1 tsp dashi powder'
      ],
      instructions: [
        'Bersihkan udang, buat sayatan di bagian perut',
        'Potong sayuran dengan ukuran seragam',
        'Buat adonan tempura: campur tepung, air es, kuning telur',
        'Jangan overmix adonan, biarkan sedikit bergerindil',
        'Panaskan minyak hingga 170°C',
        'Celup bahan ke adonan, goreng hingga golden',
        'Tiriskan di kertas minyak',
        'Buat tentsuyu dengan merebus semua bumbu sauce'
      ],
      prepTime: 20,
      cookTime: 15,
      servings: 2,
      difficulty: 'Medium',
      image: 'https://readdy.ai/api/search-image?query=Professional%20tempura%20preparation%2C%20golden%20crispy%20shrimp%20and%20vegetable%20tempura%2C%20light%20batter%20coating%2C%20deep%20frying%20technique%2C%20tentsuyu%20dipping%20sauce%2C%20restaurant%20kitchen%20setup%2C%20Japanese%20cooking%20method&width=400&height=300&seq=recipe-tempura-premium&orientation=landscape',
      tips: 'Kunci tempura crispy adalah adonan dingin dan minyak panas. Jangan overmix adonan.'
    },
    {
      id: 'matcha-latte-premium',
      name: 'Matcha Latte Premium Uji',
      category: 'Beverages',
      ingredients: [
        '2 tsp matcha powder grade A',
        '60ml air panas (70°C)',
        '200ml susu segar',
        '1 sdm gula sirup',
        'Es batu (untuk iced version)',
        'Foam art stencil'
      ],
      instructions: [
        'Ayak matcha powder untuk menghilangkan gumpalan',
        'Tuang air panas 70°C ke matcha powder',
        'Kocok dengan chasen (bamboo whisk) hingga berbusa',
        'Panaskan susu hingga 65°C, buat microfoam',
        'Tuang matcha ke cup, tambahkan sirup',
        'Pour steamed milk dengan latte art technique',
        'Buat foam art dengan stencil jika perlu',
        'Sajikan segera dengan temperatura optimal'
      ],
      prepTime: 8,
      cookTime: 5,
      servings: 1,
      difficulty: 'Easy',
      image: 'https://readdy.ai/api/search-image?query=Professional%20matcha%20latte%20preparation%2C%20authentic%20Uji%20matcha%20powder%2C%20traditional%20bamboo%20whisk%2C%20steamed%20milk%20pouring%2C%20beautiful%20foam%20art%2C%20ceramic%20cup%2C%20Japanese%20tea%20ceremony%20aesthetic%2C%20cafe%20environment&width=400&height=300&seq=recipe-matcha-latte&orientation=landscape',
      tips: 'Gunakan air 70°C untuk matcha agar tidak pahit. Susu jangan sampai mendidih.'
    }
  ]);

  const [newRecipe, setNewRecipe] = useState<Partial<Recipe>>({
    name: '',
    category: 'Makanan',
    ingredients: [''],
    instructions: [''],
    prepTime: 10,
    cookTime: 15,
    servings: 1,
    difficulty: 'Easy',
    tips: ''
  });

  const categories = ['all', 'Ramen', 'Sushi', 'Tempura', 'Appetizer', 'Beverages', 'Makanan', 'Minuman'];

  const filteredRecipes = activeCategory === 'all' 
    ? recipes 
    : recipes.filter(recipe => recipe.category === activeCategory);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openRecipeModal = () => {
    setIsModalOpen(true);
  };

  const closeRecipeModal = () => {
    setIsModalOpen(false);
    setSelectedRecipe(null);
    setShowAddRecipe(false);
    setEditingRecipe(null);
  };

  const selectRecipe = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
  };

  const backToList = () => {
    setSelectedRecipe(null);
    setShowAddRecipe(false);
    setEditingRecipe(null);
  };

  const addIngredient = () => {
    if (editingRecipe) {
      setEditingRecipe({
        ...editingRecipe,
        ingredients: [...editingRecipe.ingredients, '']
      });
    } else {
      setNewRecipe({
        ...newRecipe,
        ingredients: [...(newRecipe.ingredients || ['']), '']
      });
    }
  };

  const removeIngredient = (index: number) => {
    if (editingRecipe) {
      setEditingRecipe({
        ...editingRecipe,
        ingredients: editingRecipe.ingredients.filter((_, i) => i !== index)
      });
    } else {
      setNewRecipe({
        ...newRecipe,
        ingredients: (newRecipe.ingredients || []).filter((_, i) => i !== index)
      });
    }
  };

  const updateIngredient = (index: number, value: string) => {
    if (editingRecipe) {
      const updated = [...editingRecipe.ingredients];
      updated[index] = value;
      setEditingRecipe({ ...editingRecipe, ingredients: updated });
    } else {
      const updated = [...(newRecipe.ingredients || [''])];
      updated[index] = value;
      setNewRecipe({ ...newRecipe, ingredients: updated });
    }
  };

  const addInstruction = () => {
    if (editingRecipe) {
      setEditingRecipe({
        ...editingRecipe,
        instructions: [...editingRecipe.instructions, '']
      });
    } else {
      setNewRecipe({
        ...newRecipe,
        instructions: [...(newRecipe.instructions || ['']), '']
      });
    }
  };

  const removeInstruction = (index: number) => {
    if (editingRecipe) {
      setEditingRecipe({
        ...editingRecipe,
        instructions: editingRecipe.instructions.filter((_, i) => i !== index)
      });
    } else {
      setNewRecipe({
        ...newRecipe,
        instructions: (newRecipe.instructions || []).filter((_, i) => i !== index)
      });
    }
  };

  const updateInstruction = (index: number, value: string) => {
    if (editingRecipe) {
      const updated = [...editingRecipe.instructions];
      updated[index] = value;
      setEditingRecipe({ ...editingRecipe, instructions: updated });
    } else {
      const updated = [...(newRecipe.instructions || [''])];
      updated[index] = value;
      setNewRecipe({ ...newRecipe, instructions: updated });
    }
  };

  const saveRecipe = () => {
    const recipeData = editingRecipe || newRecipe;
    
    if (!recipeData.name || !recipeData.category) {
      alert('Nama resep dan kategori wajib diisi!');
      return;
    }

    if (!recipeData.ingredients?.some(ing => ing.trim()) || !recipeData.instructions?.some(inst => inst.trim())) {
      alert('Minimal harus ada 1 bahan dan 1 langkah!');
      return;
    }

    const finalRecipe: Recipe = {
      id: editingRecipe?.id || 'recipe-' + Date.now(),
      name: recipeData.name!,
      category: recipeData.category!,
      ingredients: recipeData.ingredients!.filter(ing => ing.trim()),
      instructions: recipeData.instructions!.filter(inst => inst.trim()),
      prepTime: recipeData.prepTime || 10,
      cookTime: recipeData.cookTime || 15,
      servings: recipeData.servings || 1,
      difficulty: recipeData.difficulty || 'Easy',
      image: `https://readdy.ai/api/search-image?query=Professional%20$%7BrecipeData.name%7D%20cooking%20preparation%20in%20restaurant%20kitchen%2C%20$%7BrecipeData.category%7D%20cuisine%2C%20chef%20hands%20cooking%2C%20steam%20rising%2C%20authentic%20cooking%20process&width=400&height=300&seq=recipe-${Date.now()}&orientation=landscape`,
      tips: recipeData.tips || ''
    };

    if (editingRecipe) {
      // Update existing recipe
      setRecipes(prev => prev.map(r => r.id === editingRecipe.id ? finalRecipe : r));
    } else {
      // Add new recipe
      setRecipes(prev => [...prev, finalRecipe]);
    }

    // Reset form
    setNewRecipe({
      name: '',
      category: 'Makanan',
      ingredients: [''],
      instructions: [''],
      prepTime: 10,
      cookTime: 15,
      servings: 1,
      difficulty: 'Easy',
      tips: ''
    });
    
    setShowAddRecipe(false);
    setEditingRecipe(null);
    alert(editingRecipe ? 'Resep berhasil diupdate!' : 'Resep baru berhasil ditambahkan!');
  };

  const deleteRecipe = (recipeId: string) => {
    const confirm = window.confirm('Yakin ingin menghapus resep ini?');
    if (confirm) {
      setRecipes(prev => prev.filter(r => r.id !== recipeId));
      if (selectedRecipe?.id === recipeId) {
        setSelectedRecipe(null);
      }
      alert('Resep berhasil dihapus!');
    }
  };

  const startEditRecipe = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setShowAddRecipe(true);
  };

  return (
    <>
      {/* Recipe Book Button */}
      <button
        onClick={openRecipeModal}
        className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-2xl p-6 transition-all duration-300 transform hover:scale-105 cursor-pointer"
      >
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
            <i className="ri-book-open-line w-6 h-6 flex items-center justify-center"></i>
          </div>
          <div className="flex-1 text-left">
            <h3 className="text-lg font-bold">Recipe Book</h3>
            <p className="text-sm text-white/80">Koleksi resep menu TAKEYA Restaurant</p>
          </div>
          <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
        </div>
      </button>

      {/* Recipe Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            
            {!selectedRecipe && !showAddRecipe ? (
              // Recipe List View
              <>
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                      <i className="ri-book-open-line text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Recipe Book TAKEYA</h2>
                      <p className="text-purple-400">Koleksi resep menu restaurant</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowAddRecipe(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
                      <span>Tambah Resep</span>
                    </button>
                    <button
                      onClick={closeRecipeModal}
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>

                {/* Category Filter */}
                <div className="p-6 border-b border-gray-700">
                  <div className="flex space-x-2 overflow-x-auto">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`px-4 py-2 rounded-xl whitespace-nowrap cursor-pointer transition-all ${
                          activeCategory === category
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {category === 'all' ? 'Semua Kategori' : category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recipe Grid */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRecipes.map((recipe) => (
                      <div
                        key={recipe.id}
                        className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-purple-500/20 overflow-hidden hover:border-purple-400/40 transition-all duration-300 transform hover:scale-105"
                      >
                        <div className="relative">
                          <img
                            src={recipe.image}
                            alt={recipe.name}
                            className="w-full h-48 object-cover object-top"
                          />
                          <div className="absolute top-4 right-4 flex space-x-2">
                            <button
                              onClick={() => startEditRecipe(recipe)}
                              className="w-8 h-8 bg-blue-600/90 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                            </button>
                            <button
                              onClick={() => deleteRecipe(recipe.id)}
                              className="w-8 h-8 bg-red-600/90 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                            </button>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getDifficultyColor(recipe.difficulty)}`}>
                              {recipe.difficulty}
                            </span>
                          </div>
                          <div className="absolute bottom-4 left-4">
                            <span className="bg-purple-600/90 text-white px-3 py-1 rounded-full text-xs font-bold">
                              {recipe.category}
                            </span>
                          </div>
                        </div>

                        <div className="p-6">
                          <h3 className="text-lg font-bold text-white mb-2">{recipe.name}</h3>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                            <div className="flex items-center space-x-1">
                              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                              <span>{recipe.prepTime + recipe.cookTime} min</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <i className="ri-group-line w-4 h-4 flex items-center justify-center"></i>
                              <span>{recipe.servings} porsi</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="text-purple-400 text-sm">
                              {recipe.ingredients.length} bahan
                            </div>
                            <button 
                              onClick={() => selectRecipe(recipe)}
                              className="text-purple-400 hover:text-purple-300 transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : showAddRecipe ? (
              // Add/Edit Recipe Form
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={backToList}
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line w-6 h-6 flex items-center justify-center"></i>
                    </button>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {editingRecipe ? 'Edit Resep' : 'Tambah Resep Baru'}
                      </h2>
                      <p className="text-purple-400">Lengkapi form untuk menambah resep</p>
                    </div>
                  </div>
                  <button
                    onClick={closeRecipeModal}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Basic Info */}
                    <div className="space-y-6">
                      <div className="bg-gray-800/30 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-4">Informasi Dasar</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-gray-300 font-semibold mb-2">Nama Resep *</label>
                            <input
                              type="text"
                              value={editingRecipe?.name || newRecipe.name || ''}
                              onChange={(e) => editingRecipe 
                                ? setEditingRecipe({...editingRecipe, name: e.target.value})
                                : setNewRecipe({...newRecipe, name: e.target.value})
                              }
                              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
                              placeholder="Contoh: Ramen Spesial"
                            />
                          </div>

                          <div>
                            <label className="block text-gray-300 font-semibold mb-2">Kategori *</label>
                            <select
                              value={editingRecipe?.category || newRecipe.category || ''}
                              onChange={(e) => editingRecipe 
                                ? setEditingRecipe({...editingRecipe, category: e.target.value})
                                : setNewRecipe({...newRecipe, category: e.target.value})
                              }
                              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:border-purple-500 focus:outline-none pr-8"
                            >
                              <option value="Makanan">Makanan</option>
                              <option value="Minuman">Minuman</option>
                              <option value="Ramen">Ramen</option>
                              <option value="Sushi">Sushi</option>
                              <option value="Tempura">Tempura</option>
                              <option value="Appetizer">Appetizer</option>
                              <option value="Beverages">Beverages</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-300 font-semibold mb-2">Waktu Persiapan (menit)</label>
                              <input
                                type="number"
                                value={editingRecipe?.prepTime || newRecipe.prepTime || ''}
                                onChange={(e) => editingRecipe 
                                  ? setEditingRecipe({...editingRecipe, prepTime: parseInt(e.target.value) || 0})
                                  : setNewRecipe({...newRecipe, prepTime: parseInt(e.target.value) || 0})
                                }
                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:border-purple-500 focus:outline-none"
                                min="1"
                              />
                            </div>
                            <div>
                              <label className="block text-gray-300 font-semibold mb-2">Waktu Memasak (menit)</label>
                              <input
                                type="number"
                                value={editingRecipe?.cookTime || newRecipe.cookTime || ''}
                                onChange={(e) => editingRecipe 
                                  ? setEditingRecipe({...editingRecipe, cookTime: parseInt(e.target.value) || 0})
                                  : setNewRecipe({...newRecipe, cookTime: parseInt(e.target.value) || 0})
                                }
                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:border-purple-500 focus:outline-none"
                                min="1"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-300 font-semibold mb-2">Porsi</label>
                              <input
                                type="number"
                                value={editingRecipe?.servings || newRecipe.servings || ''}
                                onChange={(e) => editingRecipe 
                                  ? setEditingRecipe({...editingRecipe, servings: parseInt(e.target.value) || 0})
                                  : setNewRecipe({...newRecipe, servings: parseInt(e.target.value) || 0})
                                }
                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:border-purple-500 focus:outline-none"
                                min="1"
                              />
                            </div>
                            <div>
                              <label className="block text-gray-300 font-semibold mb-2">Tingkat Kesulitan</label>
                              <select
                                value={editingRecipe?.difficulty || newRecipe.difficulty || ''}
                                onChange={(e) => editingRecipe 
                                  ? setEditingRecipe({...editingRecipe, difficulty: e.target.value as Recipe['difficulty']})
                                  : setNewRecipe({...newRecipe, difficulty: e.target.value as Recipe['difficulty']})
                                }
                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:border-purple-500 focus:outline-none pr-8"
                              >
                                <option value="Easy">Easy</option>
                                <option value="Medium">Medium</option>
                                <option value="Hard">Hard</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-300 font-semibold mb-2">Tips Chef (opsional)</label>
                            <textarea
                              value={editingRecipe?.tips || newRecipe.tips || ''}
                              onChange={(e) => editingRecipe 
                                ? setEditingRecipe({...editingRecipe, tips: e.target.value})
                                : setNewRecipe({...newRecipe, tips: e.target.value})
                              }
                              rows={3}
                              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none resize-none"
                              placeholder="Tips khusus untuk resep ini..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ingredients & Instructions */}
                    <div className="space-y-6">
                      {/* Ingredients */}
                      <div className="bg-gray-800/30 rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-white">Bahan-Bahan *</h3>
                          <button
                            onClick={addIngredient}
                            className="text-green-400 hover:text-green-300 cursor-pointer"
                          >
                            <i className="ri-add-line w-5 h-5 flex items-center justify-center"></i>
                          </button>
                        </div>
                        
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {(editingRecipe?.ingredients || newRecipe.ingredients || ['']).map((ingredient, index) => (
                            <div key={index} className="flex space-x-2">
                              <input
                                type="text"
                                value={ingredient}
                                onChange={(e) => updateIngredient(index, e.target.value)}
                                className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none text-sm"
                                placeholder="Contoh: 200g tepung terigu"
                              />
                              <button
                                onClick={() => removeIngredient(index)}
                                className="text-red-400 hover:text-red-300 cursor-pointer"
                              >
                                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Instructions */}
                      <div className="bg-gray-800/30 rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-white">Langkah Memasak *</h3>
                          <button
                            onClick={addInstruction}
                            className="text-green-400 hover:text-green-300 cursor-pointer"
                          >
                            <i className="ri-add-line w-5 h-5 flex items-center justify-center"></i>
                          </button>
                        </div>
                        
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {(editingRecipe?.instructions || newRecipe.instructions || ['']).map((instruction, index) => (
                            <div key={index} className="flex space-x-2">
                              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                                {index + 1}
                              </div>
                              <textarea
                                value={instruction}
                                onChange={(e) => updateInstruction(index, e.target.value)}
                                rows={2}
                                className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none text-sm resize-none"
                                placeholder="Langkah memasak..."
                              />
                              <button
                                onClick={() => removeInstruction(index)}
                                className="text-red-400 hover:text-red-300 cursor-pointer mt-1"
                              >
                                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-700">
                    <button
                      onClick={backToList}
                      className="px-6 py-3 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Batal
                    </button>
                    <button
                      onClick={saveRecipe}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {editingRecipe ? 'Update Resep' : 'Simpan Resep'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // Recipe Detail View (existing code)
              <>
                {/* Recipe Detail Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={backToList}
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line w-6 h-6 flex items-center justify-center"></i>
                    </button>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedRecipe.name}</h2>
                      <p className="text-purple-400">{selectedRecipe.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => startEditRecipe(selectedRecipe)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => deleteRecipe(selectedRecipe.id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                      <span>Hapus</span>
                    </button>
                    <button
                      onClick={closeRecipeModal}
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>

                {/* Recipe Detail Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
                  <div className="p-6 space-y-8">
                    
                    {/* Recipe Image & Info */}
                    <div className="grid lg:grid-cols-2 gap-8">
                      <div>
                        <img
                          src={selectedRecipe.image}
                          alt={selectedRecipe.name}
                          className="w-full h-64 object-cover object-top rounded-2xl"
                        />
                      </div>
                      
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                            <i className="ri-time-line text-purple-400 w-6 h-6 flex items-center justify-center mx-auto mb-2"></i>
                            <div className="text-white font-bold">{selectedRecipe.prepTime} min</div>
                            <div className="text-gray-400 text-sm">Prep Time</div>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                            <i className="ri-fire-line text-red-400 w-6 h-6 flex items-center justify-center mx-auto mb-2"></i>
                            <div className="text-white font-bold">{selectedRecipe.cookTime} min</div>
                            <div className="text-gray-400 text-sm">Cook Time</div>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                            <i className="ri-group-line text-green-400 w-6 h-6 flex items-center justify-center mx-auto mb-2"></i>
                            <div className="text-white font-bold">{selectedRecipe.servings}</div>
                            <div className="text-gray-400 text-sm">Servings</div>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                            <i className="ri-bar-chart-line text-yellow-400 w-6 h-6 flex items-center justify-center mx-auto mb-2"></i>
                            <div className="text-white font-bold">{selectedRecipe.difficulty}</div>
                            <div className="text-gray-400 text-sm">Level</div>
                          </div>
                        </div>

                        {selectedRecipe.tips && (
                          <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
                            <h4 className="text-purple-400 font-semibold mb-2 flex items-center">
                              <i className="ri-lightbulb-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                              Chef's Tips
                            </h4>
                            <p className="text-gray-300 text-sm">{selectedRecipe.tips}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Ingredients & Instructions */}
                    <div className="grid lg:grid-cols-2 gap-8">
                      
                      {/* Ingredients */}
                      <div className="bg-gray-800/30 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                          <i className="ri-shopping-cart-line mr-3 w-5 h-5 flex items-center justify-center text-purple-400"></i>
                          Bahan-Bahan
                        </h3>
                        <ul className="space-y-3">
                          {selectedRecipe.ingredients.map((ingredient, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-300">{ingredient}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Instructions */}
                      <div className="bg-gray-800/30 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                          <i className="ri-list-ordered mr-3 w-5 h-5 flex items-center justify-center text-green-400"></i>
                          Cara Memasak
                        </h3>
                        <ol className="space-y-4">
                          {selectedRecipe.instructions.map((instruction, index) => (
                            <li key={index} className="flex space-x-4">
                              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {index + 1}
                              </div>
                              <span className="text-gray-300 pt-1">{instruction}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
